/**
 * Extracts initials from a full name
 * @param fullName - The full name string
 * @returns A string with 1-2 uppercase initials
 * @throws Error if input is not a non-empty string
 */
export function getInitials(fullName: string): string {
  if (typeof fullName !== "string" || fullName.trim() === "") {
    return "IP";
  }

  const words: string[] = fullName.trim().split(/\s+/);

  if (words.length === 1) {
    return words[0][0].toUpperCase();
  }

  return (words[0][0] + words[1][0]).toUpperCase();
}


/** RFC 5322–style email validation (relaxed). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (value: string): boolean => {
  return EMAIL_REGEX.test(value.trim());
}
/** "COMPLETED" / "no_answer" → "Completed" / "No answer". */
export const humanize = (value: string): string => {
  const text = value.replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Typed plain text → editor HTML: one escaped <p> per line, so text that
 * merely looks like markup ("<test>", "a & b") is stored literally.
 */
export function plainTextToHtml(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) =>
      `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`
    )
    .join("");
}

/**
 * HTML fragment → readable plain text that keeps paragraph / line breaks and
 * decodes common entities (for tooltips like a task's note on hover).
 */
export function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Flattens an HTML fragment to plain text (for one-line previews). */
export function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
